import { MantineProvider, rem } from '@mantine/core';

export const lavenderTheme = (mode = 'light') => ({
  /** 🌈 COLOR PALETTE (MATCHING MUI THEME) */
 colors: {
  primary: [
    '#F8F5FB', // Lightest (0)
    '#EDE3F8', // Lighter (1)
    '#E0CFF5', // Light (2)
    '#D2B8F0', // Medium-light (3)
    '#C39EEA', // Medium (4)
    '#B57EDC', // Primary shade (5)
    '#A06AD0', // Dark (6)
    '#8B55C3', // Darker (7)
    '#763FAF', // Darkest (8)
    '#5E2F91', // Very dark (9)
  ],
  secondary: [
    '#F4F7FB', // Lightest (0)
    '#E3ECF9', // Lighter (1)
    '#D1E0F6', // Light (2)
    '#BBD3F2', // Medium-light (3)
    '#A7C7E7', // Medium (4)
    '#8EB8E0', // Secondary shade (5)
    '#7AA6D4', // Dark (6)
    '#6493C7', // Darker (7)
    '#4E7FB8', // Darkest (8)
    '#3B6CA3', // Very dark (9)
  ],
  neutral: [
    '#FAFAFB', // Lightest (0)
    '#F2F2F5', // Lighter (1)
    '#E8E6EC', // Light (2)
    '#D7D3E0', // Medium-light (3)
    '#BEB8CB', // Medium (4)
    '#A59EB5', // Neutral shade (5)
    '#7E7891', // Dark (6)
    '#5C556F', // Darker (7)
    '#3D3751', // Darkest (8)
    '#1F1A2E', // Very dark (9)
  ],
  success: [
    '#F1FAF3', // Lightest (0)
    '#D8F3DD', // Lighter (1)
    '#BFECC7', // Light (2)
    '#A2E1AE', // Medium-light (3)
    '#83D695', // Medium (4)
    '#66BB6A', // Success shade (5)
    '#4CA857', // Dark (6)
    '#3B9447', // Darker (7)
    '#2E7D32', // Darkest (8)
    '#1C5A21', // Very dark (9)
  ],
  warning: [
    '#FFF9F0', // Lightest (0)
    '#FFEFD3', // Lighter (1)
    '#FFE4B2', // Light (2)
    '#FFD78A', // Medium-light (3)
    '#FFC965', // Medium (4)
    '#FFB74D', // Warning shade (5)
    '#FFA63B', // Dark (6)
    '#FF9829', // Darker (7)
    '#FF8A00', // Darkest (8)
    '#E67A00', // Very dark (9)
  ],
  error: [
    '#FFF2F3', // Lightest (0)
    '#FFE0E3', // Lighter (1)
    '#FFCDD2', // Light (2)
    '#F8A9AF', // Medium-light (3)
    '#F2848D', // Medium (4)
    '#E76C70', // Error shade (5)
    '#D63E44', // Dark (6)
    '#C93036', // Darker (7)
    '#B71C1C', // Darkest (8)
    '#8A1212', // Very dark (9)
  ],
},

  primaryColor: 'primary',
  primaryShade: { light: 5, dark: 6 },
  secondaryColor: 'secondary',

  /** 🌙 LIGHT & DARK MODE */
  colorScheme: mode,
  autoContrast: true,
  respectReducedMotion: true,
  focusRing: 'auto',
  defaultGradient: {
    from: mode === 'light' ? '#26A69A' : '#4DD0E1',
    to: mode === 'light' ? '#AB47BC' : '#E1BEE7',
    deg: 135,
  },
  luminanceThreshold: 0.3,
  white: mode === 'light' ? '#FFFFFF' : '#EDF7FA',
  black: mode === 'light' ? '#00695C' : '#1A1A1A',

  /** 📖 TYPOGRAPHY (MATCHING MUI THEME) */
  fontFamily: '"Nunito Sans", Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  headings: {
    fontFamily: '"Poppins", Inter, sans-serif',
    fontWeight: 700,
    sizes: {
      h1: { fontSize: { base: rem(28), xs: rem(28), sm: rem(32), md: rem(36), lg: rem(40), xl: rem(48), xxl: rem(56) }, lineHeight: '1.2' },
      h2: { fontSize: { base: rem(24), xs: rem(24), sm: rem(28), md: rem(32), lg: rem(36), xl: rem(40), xxl: rem(48) }, lineHeight: '1.3' },
      h3: { fontSize: { base: rem(20), xs: rem(20), sm: rem(24), md: rem(28), lg: rem(32), xl: rem(36), xxl: rem(40) }, lineHeight: '1.4' },
      h4: { fontSize: { base: rem(18), xs: rem(18), sm: rem(20), md: rem(24), lg: rem(28), xl: rem(32), xxl: rem(36) }, lineHeight: '1.4' },
      h5: { fontSize: { base: rem(16), xs: rem(16), sm: rem(18), md: rem(20), lg: rem(24), xl: rem(28), xxl: rem(32) }, lineHeight: '1.5' },
      h6: { fontSize: { base: rem(14), xs: rem(14), sm: rem(16), md: rem(18), lg: rem(20), xl: rem(24), xxl: rem(28) }, lineHeight: '1.5' },
    },
  },

  fontSizes: {
    xs: rem(12),
    sm: rem(14),
    md: rem(16),
    lg: rem(18),
    xl: rem(20),
    xxl: rem(24),
  },

  /** 🌿 RADIUS & SHADOWS (MATCHING MUI THEME) */
  radius: {
    xs: rem(4),
    sm: rem(8),
    md: rem(16),
    lg: rem(24),
    xl: rem(50),
  },

  shadows: {
    xs: mode === 'light' ? '0 1px 2px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.1)' : '0 1px 2px rgba(0, 0, 0, 0.3)',
    sm: mode === 'light' ? '0 4px 6px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.1)' : '0 4px 6px rgba(0, 0, 0, 0.4)',
    md: mode === 'light' ? '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.05)' : '0 10px 25px rgba(0, 0, 0, 0.5)',
    lg: mode === 'light' ? '0 20px 40px rgba(0, 0, 0, 0.1), 0 8px 16px rgba(0, 0, 0, 0.05)' : '0 20px 40px rgba(0, 0, 0, 0.6)',
    xl: mode === 'light' ? '0 25px 50px rgba(0, 0, 0, 0.15), 0 10px 20px rgba(0, 0, 0, 0.1)' : '0 25px 50px rgba(0, 0, 0, 0.7)',
  },

  /** 📱 BREAKPOINTS (MATCHING MUI THEME) */
  breakpoints: {
    xs: '0px',    // 0px
    sm: '600px',  // Small
    md: '900px',  // Medium
    lg: '1200px', // Large
    xl: '1536px', // Extra-large
    xxl: '1920px', // Extra-extra-large
  },

  /** 🎨 COMPONENTS */
  components: {
    /** BUTTON */
    Button: {
      defaultProps: {
        radius: 'xl',
        size: 'md',
        variant: 'gradient',
      },
      styles: (theme) => ({
        root: {
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 500,
          fontSize: rem(14),
          padding: `${rem(8)} ${rem(16)}`,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: theme.shadows.sm,
          },
          '&:focus': {
            outline: `2px solid ${theme.colors.primary[5]}`,
            outlineOffset: '2px',
          },
          '&[data-variant="gradient"]': {
            backgroundImage: theme.defaultGradient,
          },
          '&[data-variant="outline"]': {
            border: `1px solid ${theme.colors.primary[5]}`,
            color: theme.colors.primary[5],
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: `${theme.colors.primary[0]}20`,
            },
          },
          '&[data-variant="filled"][data-color="error"]': {
            backgroundColor: theme.colors.error[5],
            '&:hover': {
              backgroundColor: theme.colors.error[6],
            },
          },
        },
      }),
    },

    /** TEXT INPUT (USED FOR SEARCH) */
    TextInput: {
      defaultProps: {
        radius: 'xl',
        size: 'md',
      },
      styles: (theme) => ({
        root: {
          position: 'relative',
        },
        input: {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: rem(14),
          height: rem(40),
          paddingLeft: rem(36),
          borderRadius: rem(50),
          border: `1px solid ${theme.colors.neutral[4]}`,
          backgroundColor: theme.white,
          color: theme.black,
          '&:focus': {
            borderColor: theme.colors.primary[5],
            boxShadow: `0 0 0 2px ${theme.colors.primary[5]}20`,
          },
          '&::placeholder': {
            color: theme.colors.neutral[5],
          },
        },
      }),
    },

    /** SELECT (USED FOR SORT/FILTER) */
    Select: {
      defaultProps: {
        radius: 'xl',
        size: 'md',
      },
      styles: (theme) => ({
        root: {
          fontFamily: '"Nunito Sans", sans-serif',
        },
        input: {
          fontSize: rem(14),
          height: rem(40),
          borderRadius: rem(50),
          border: `1px solid ${theme.colors.neutral[4]}`,
          backgroundColor: theme.white,
          color: theme.black,
          paddingRight: rem(32),
          '&:focus': {
            borderColor: theme.colors.primary[5],
            boxShadow: `0 0 0 2px ${theme.colors.primary[5]}20`,
          },
        },
        dropdown: {
          borderRadius: rem(16),
          boxShadow: theme.shadows.md,
          backgroundColor: theme.white,
          border: `1px solid ${theme.colors.neutral[4]}`,
        },
        item: {
          fontSize: rem(14),
          color: theme.black,
          padding: `${rem(8)} ${rem(12)}`,
          '&:hover': {
            backgroundColor: `${theme.colors.primary[0]}20`,
          },
          '&[data-selected]': {
            backgroundColor: theme.colors.primary[5],
            color: theme.white,
            '&:hover': {
              backgroundColor: theme.colors.primary[6],
            },
          },
        },
        rightSection: {
          color: theme.colors.primary[5],
          width: rem(24),
        },
      }),
    },

    /** MODAL */
    Modal: {
      defaultProps: {
        radius: 'lg',
        shadow: 'lg',
        centered: true,
      },
      styles: (theme) => ({
        content: {
          borderRadius: rem(24),
          backgroundColor: theme.white,
          border: `1px solid ${theme.colors.neutral[4]}`,
          boxShadow: theme.shadows.lg,
          padding: rem(24),
        },
        header: {
          padding: 0,
          marginBottom: rem(16),
        },
        title: {
          fontFamily: '"Poppins", sans-serif',
          fontSize: rem(24),
          fontWeight: 700,
          color: theme.black,
        },
        close: {
          color: theme.colors.primary[5],
          '&:hover': {
            backgroundColor: `${theme.colors.primary[0]}20`,
          },
        },
      }),
    },

    /** CARD (USED FOR FILE TYPE CARDS) */
    Card: {
      defaultProps: {
        radius: 'md',
        shadow: 'sm',
        withBorder: true,
      },
      styles: (theme) => ({
        root: {
          borderRadius: rem(16),
          border: `1px solid ${theme.colors.neutral[4]}`,
          backgroundColor: theme.white,
          padding: rem(16),
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: theme.shadows.md,
            transform: 'scale(1.01)',
          },
        },
      }),
    },

    /** TABLE (USED IF FILE INSIGHTS DISPLAYED AS TABLE) */
    Table: {
      defaultProps: {
        striped: 'even',
        highlightOnHover: true,
        withBorder: true,
        withColumnBorders: true,
      },
      styles: (theme) => ({
        root: {
          borderRadius: rem(16),
          overflow: 'hidden',
          background: mode === 'light'
            ? `linear-gradient(145deg, ${theme.white}, ${theme.colors.primary[0]}60)`
            : `linear-gradient(145deg, ${theme.colors.neutral[8]}, ${theme.colors.neutral[9]}80)`,
          border: `1px solid ${theme.colors.primary[5]}20`,
          boxShadow: theme.shadows.md,
        },
        table: {
          borderCollapse: 'separate',
          borderSpacing: 0,
          minWidth: '100%',
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: rem(14),
        },
        thead: {
          background: mode === 'light'
            ? `linear-gradient(135deg, ${theme.colors.primary[5]}15, ${theme.colors.secondary[5]}10)`
            : `linear-gradient(135deg, ${theme.colors.primary[6]}20, ${theme.colors.secondary[6]}30)`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        },
        th: {
          padding: `${rem(16)} ${rem(20)}`,
          fontWeight: 700,
          fontSize: rem(14),
          color: mode === 'light' ? theme.colors.primary[7] : theme.colors.primary[2],
          background: 'transparent',
          borderBottom: `2px solid ${theme.colors.primary[5]}20`,
          borderRight: `1px solid ${theme.colors.primary[5]}10`,
          textAlign: 'left',
          '&:last-child': { borderRight: 'none' },
        },
        tbody: {
          background: 'transparent',
        },
        tr: {
          transition: 'all 0.2s ease',
          borderBottom: `1px solid ${theme.colors.primary[5]}10`,
          '&:hover': {
            background: mode === 'light' ? `${theme.colors.primary[0]}20` : `${theme.colors.primary[9]}20`,
            transform: 'scale(1.01)',
          },
          '&:last-child': { borderBottom: 'none' },
        },
        td: {
          padding: `${rem(16)} ${rem(20)}`,
          color: mode === 'light' ? theme.black : theme.white,
          borderRight: `1px solid ${theme.colors.primary[5]}10`,
          '&:last-child': { borderRight: 'none' },
        },
        '[data-stripe="even"]': {
          background: mode === 'light' ? `${theme.colors.primary[0]}10` : `${theme.colors.primary[9]}10`,
        },
        '[data-stripe="odd"]': {
          background: mode === 'light' ? 'transparent' : `${theme.colors.primary[8]}10`,
        },
      }),
    },

    /** NOTIFICATION (USED FOR SNACKBAR) */
    Notification: {
      styles: (theme) => ({
        root: {
          borderRadius: rem(8),
          boxShadow: theme.shadows.sm,
          border: `1px solid ${theme.colors.neutral[4]}`,
          backgroundColor: theme.white,
          padding: rem(16),
        },
        title: {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: rem(14),
          fontWeight: 600,
          color: theme.black,
        },
        description: {
          fontFamily: '"Nunito Sans", sans-serif',
          fontSize: rem(14),
          color: theme.black,
        },
        closeButton: {
          color: theme.colors.primary[5],
          '&:hover': {
            backgroundColor: `${theme.colors.primary[0]}20`,
          },
        },
      }),
    },

    /** LOADER (USED FOR CIRCULAR PROGRESS) */
    Loader: {
      defaultProps: {
        size: 'sm',
        variant: 'oval',
      },
      styles: (theme) => ({
        root: {
          color: theme.colors.primary[5],
        },
      }),
    },
  },
});